/**
 * PlanEnrollModal – shows plans, payment details (bank/UPI/QR), screenshot upload,
 * and submits enrollment. Opened when customer needs a plan.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, CheckCircle, Crown, Clock, RefreshCw, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) { return Number(n || 0).toLocaleString("en-IN"); }

function PlanCard({ plan, selected, onSelect }) {
  const free = Number(plan.price) === 0;
  const dl   = plan.download_limit === -1 ? "Unlimited" : plan.download_limit;
  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      onClick={() => onSelect(plan)}
      className={`relative cursor-pointer rounded-2xl border-2 p-5 transition-all ${
        selected ? "border-orange-500 bg-orange-50 shadow-lg" : "border-slate-200 bg-white hover:border-orange-300"
      }`}
    >
      {selected && (
        <CheckCircle className="absolute top-3 right-3 w-5 h-5 text-orange-500" />
      )}
      <div className="flex items-center gap-2 mb-2">
        <Crown className="w-4 h-4 text-orange-400" />
        <h3 className="font-bold text-slate-800">{plan.name}</h3>
      </div>
      <div className="text-2xl font-black text-slate-900 mb-1">
        {free ? "Free" : `₹${fmt(plan.price)}`}
        {!free && (
          <span className="text-sm font-normal text-slate-400 ml-1">
            / {plan.billing}
          </span>
        )}
      </div>
      <div className="text-xs text-slate-500 mb-3">
        {plan.validity_days} days · {dl} downloads
      </div>
      {(plan.features || []).length > 0 && (
        <ul className="space-y-1">
          {plan.features.slice(0, 4).map((f, i) => (
            <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
              {f}
            </li>
          ))}
        </ul>
      )}
      {plan.gst_type === "exclusive" && plan.gst_percent > 0 && (
        <p className="text-[11px] text-slate-400 mt-2">+ {plan.gst_percent}% GST</p>
      )}
    </div>
  );
}

export default function PlanEnrollModal({ token, onClose, onSuccess }) {
  const [step, setStep]             = useState("plans"); // plans | payment | uploading | done
  const [plans, setPlans]           = useState([]);
  const [settings, setSettings]     = useState(null);
  const [selectedPlan, setSelected] = useState(null);
  const [payMethod, setPayMethod]   = useState("upi");
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotUrl, setScUrl]   = useState("");
  const [notes, setNotes]           = useState("");
  const [uploading, setUploading]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/subscription/plans`),
      axios.get(`${API}/subscription/settings`),
    ]).then(([p, s]) => {
      setPlans(p.data);
      setSettings(s.data);
      if (p.data.length > 0) setSelected(p.data[0]);
    }).catch(() => toast.error("Failed to load plans"))
      .finally(() => setLoadingPlans(false));
  }, []);

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File must be under 5 MB"); return; }
    setScreenshot(f);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/customer/upload/screenshot`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setScUrl(res.data.url);
      toast.success("Screenshot uploaded");
    } catch (e) {
      toast.error("Upload failed. You can still submit – admin will follow up.");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedPlan) { toast.error("Select a plan"); return; }
    if (!screenshotUrl && !notes) { toast.error("Upload screenshot or add a payment note"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/customer/subscription/enroll`, {
        plan_id: selectedPlan.id,
        payment_method: payMethod,
        payment_screenshot_url: screenshotUrl,
        payment_notes: notes,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setStep("done");
    } catch (e) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success("Copied!"); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between rounded-t-3xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-orange-500 flex items-center justify-center">
              <Crown className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900">
                {step === "done" ? "Enrollment Submitted!" : "Subscribe to a Plan"}
              </h2>
              {step !== "done" && (
                <p className="text-xs text-slate-400">
                  {step === "plans" ? "Choose a plan" : "Complete your payment"}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-6">

          {/* ── STEP: done ── */}
          {step === "done" && (
            <div className="text-center py-6 space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-9 h-9 text-green-500" />
              </div>
              <h3 className="text-xl font-black text-slate-900">Awaiting Confirmation</h3>
              <p className="text-sm text-slate-500 max-w-sm mx-auto">
                Your enrollment request for <strong>{selectedPlan?.name}</strong> has been submitted.
                The admin will verify your payment and activate your plan shortly.
              </p>
              <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-sm text-orange-700 text-left">
                <Clock className="w-4 h-4 inline mr-1.5" />
                You'll be able to see your plan status in <strong>My Plans</strong> once activated.
              </div>
              <button onClick={() => { onSuccess && onSuccess(); onClose(); }}
                data-testid="enrollment-done-btn"
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-xl transition-colors">
                Back to My Account
              </button>
            </div>
          )}

          {/* ── STEP: plans ── */}
          {step === "plans" && (
            <div className="space-y-4">
              {loadingPlans ? (
                <div className="flex items-center justify-center h-32">
                  <RefreshCw className="w-6 h-6 text-orange-500 animate-spin" />
                </div>
              ) : plans.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  No plans available. Please contact support.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {plans.map(p => (
                      <PlanCard key={p.id} plan={p} selected={selectedPlan?.id === p.id} onSelect={setSelected} />
                    ))}
                  </div>
                  {selectedPlan && Number(selectedPlan.price) > 0 && (
                    <button
                      data-testid="proceed-to-payment-btn"
                      onClick={() => setStep("payment")}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-xl transition-colors mt-2">
                      Proceed to Payment →
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── STEP: payment ── */}
          {step === "payment" && settings && selectedPlan && (
            <div className="space-y-5">
              {/* Selected plan summary */}
              <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Selected Plan</p>
                  <p className="font-bold text-slate-800">{selectedPlan.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-black text-orange-600">₹{fmt(selectedPlan.price)}</p>
                  {selectedPlan.gst_type === "exclusive" && selectedPlan.gst_percent > 0 && (
                    <p className="text-[11px] text-slate-400">+ {selectedPlan.gst_percent}% GST</p>
                  )}
                </div>
              </div>

              {/* Payment method tabs */}
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2 block">Payment Method</label>
                <div className="flex gap-2">
                  {["upi", "bank_transfer"].map(m => (
                    <button key={m} onClick={() => setPayMethod(m)}
                      className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                        payMethod === m ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}>
                      {m === "upi" ? "UPI / QR" : "Bank Transfer"}
                    </button>
                  ))}
                </div>
              </div>

              {/* UPI / QR */}
              {payMethod === "upi" && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                  {settings.payment_qr_url && (
                    <div className="flex flex-col items-center gap-2">
                      <img src={settings.payment_qr_url} alt="UPI QR Code"
                        className="w-44 h-44 object-contain border border-slate-200 rounded-xl bg-white p-2" />
                      <p className="text-xs text-slate-400">Scan with any UPI app</p>
                    </div>
                  )}
                  {settings.payment_upi_id && (
                    <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-3">
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase font-bold">UPI ID</p>
                        <p className="font-mono font-bold text-slate-800">{settings.payment_upi_id}</p>
                      </div>
                      <button onClick={() => copyText(settings.payment_upi_id)}
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                        <Copy className="w-4 h-4 text-slate-400" />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Bank Transfer */}
              {payMethod === "bank_transfer" && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                  {[
                    ["Bank Name",       settings.payment_bank_name],
                    ["Account Name",    settings.payment_account_name],
                    ["Account Number",  settings.payment_account_number],
                    ["IFSC Code",       settings.payment_ifsc],
                    ["Branch",          settings.payment_bank_branch],
                  ].filter(([,v]) => v).map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-2.5">
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase font-bold">{label}</p>
                        <p className="font-mono font-semibold text-slate-800 text-sm">{value}</p>
                      </div>
                      <button onClick={() => copyText(value)}
                        className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
                        <Copy className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {settings.payment_instructions && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700">
                  {settings.payment_instructions}
                </div>
              )}

              {/* Screenshot upload */}
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2 block">
                  Upload Payment Screenshot <span className="text-orange-500">*</span>
                </label>
                <label className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl py-6 cursor-pointer transition-colors ${
                  screenshotUrl ? "border-green-400 bg-green-50" : "border-slate-300 hover:border-orange-400 bg-slate-50"
                }`}>
                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleFileChange} />
                  {uploading ? (
                    <RefreshCw className="w-6 h-6 text-orange-500 animate-spin" />
                  ) : screenshotUrl ? (
                    <>
                      <CheckCircle className="w-6 h-6 text-green-500" />
                      <p className="text-sm font-semibold text-green-600">Screenshot uploaded</p>
                      <p className="text-xs text-slate-400">{screenshot?.name}</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-slate-400" />
                      <p className="text-sm text-slate-500">Click to upload payment proof</p>
                      <p className="text-xs text-slate-400">JPG, PNG, PDF · max 5 MB</p>
                    </>
                  )}
                </label>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5 block">
                  Payment Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="e.g. UTR number, transaction ID, or any message to admin"
                  rows={2}
                  className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:border-orange-400 resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep("plans")}
                  className="flex-1 border-2 border-slate-200 hover:border-slate-400 text-slate-600 font-semibold py-3 rounded-xl transition-colors">
                  ← Back
                </button>
                <button
                  data-testid="submit-enrollment-btn"
                  onClick={handleSubmit}
                  disabled={submitting || uploading}
                  className="flex-2 flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-3 rounded-xl transition-colors">
                  {submitting ? "Submitting…" : "Submit for Approval →"}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
